import React, { useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import EmojiPicker from 'emoji-picker-react';
import { HIDDEN_EMOJIS } from '../../../../constants/main';

const emojiPickerStyle = {
  '--epr-emoji-size': '20px',
  '--epr-emoji-padding': '2px',
  '--epr-category-navigation-button-size': '23px',
  '--epr-header-padding': '5px',
  position: 'absolute',
  top: `0px`,
  left: `0px`,
  zIndex: 1000
};

const emojieCategories = [
  {
    category: 'smileys_people',
    name: 'filter-people'
  },
  {
    category: 'animals_nature',
    name: 'filter-nature'
  },
  {
    category: 'food_drink',
    name: 'filter-food'
  },
  {
    category: 'symbols',
    name: 'filter-symbols'
  },
  {
    category: 'activities',
    name: 'filter-activity'
  },
  {
    category: 'travel_places',
    name: 'filter-travel'
  },
  {
    category: 'objects',
    name: 'filter-objects'
  },
  {
    category: 'flags',
    name: 'filter-flags'
  }
];

const RichTextEditorEmojiPicker = ({
  isOpen,
  position,
  onClickOutside,
  onEmojiSelect,
  toolbarButtonSelector = '',
  containerSelector = 'body',
  quill
}) => {
  const dropdownRef = useRef(null);
  const justOpenedRef = useRef(false);

  // Handle opening state with a short delay to prevent closing on the same click
  useEffect(() => {
    if (isOpen) {
      justOpenedRef.current = true;

      const timer = setTimeout(() => {
        justOpenedRef.current = false;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle clicking outside
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (justOpenedRef.current) {
        return;
      }

      const isInsideDropdown = dropdownRef.current
                              && dropdownRef.current.contains(event.target);

      const isToolbarButton = event.target.closest('.ql-toolbar button')?.classList?.contains(toolbarButtonSelector);
      const isInsideToolbarModal = event.target.closest('.ant-modal')?.classList?.contains('ql-modal');
      const isInsideToolbarPopover = event.target.closest('.ant-popover')?.classList?.contains('ql-custom-link-popover');
      if (!isInsideDropdown && !isInsideToolbarModal && !isInsideToolbarPopover && !isToolbarButton) {
        onClickOutside();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClickOutside, toolbarButtonSelector]);

  const container = useMemo(() => {
    if(quill?.editor?.container) {
      return quill.editor.container;
    }
    return containerSelector === 'body'
      ? document.body
      : document.querySelector(containerSelector);
  }, [containerSelector]);

  if (!isOpen) {
    return null;
  }

  const handleEmojiClick = (emoji) => {
    onEmojiSelect(emoji.emoji);
  };

  return createPortal(
    <div
      id="emoji-pallete"
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 1000
      }}
    >
      <EmojiPicker
        categories={emojieCategories}
        emojiStyle={'native'}
        searchDisabled={true}
        hiddenEmojis={HIDDEN_EMOJIS}
        previewConfig={{ showPreview: false }}
        skinTonesDisabled width={248} height={155} style={emojiPickerStyle} open={isOpen} rows={5} perRow={8} onEmojiClick={handleEmojiClick}/>
    </div>,
    container
  );
};

RichTextEditorEmojiPicker.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  position: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired
  }).isRequired,
  onEmojiSelect: PropTypes.func,
  onClickOutside: PropTypes.func.isRequired,
  toolbarButtonSelector: PropTypes.string,
  containerSelector: PropTypes.string,
  quill: PropTypes.object
};

export default RichTextEditorEmojiPicker;
